import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const DEFAULT_SCHEMA = {
  eyebrow: "Stay in the loop",
  heading: "Join the text & email list.",
  subheading: "Get weekly pop-up locations, ask about catering, or just say hi. We read every message.",
  submit_label: "Stay Updated",
  consent_text: "By submitting you agree to receive occasional updates. Unsubscribe anytime.",
  fields: [],
  confirmation: {
    eyebrow: "Thanks!",
    title: "Got it — we'll be in touch.",
    body: "Thanks for connecting. Keep an eye out for pop-up updates — and if you sent us a message, we'll be in touch soon!",
    button_label: "Send another message",
  },
};

const initialValuesFor = (fields) =>
  Object.fromEntries(
    (fields || []).filter((f) => f.enabled).map((f) => [f.key, f.type === "checkbox" ? false : ""]),
  );

export const ContactForm = () => {
  const [schema, setSchema] = useState(DEFAULT_SCHEMA);
  const [loadingSchema, setLoadingSchema] = useState(true);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/form");
        if (!cancelled) {
          setSchema(r.data);
          setValues(initialValuesFor(r.data.fields));
        }
      } catch {
        if (!cancelled) setValues({});
      } finally {
        if (!cancelled) setLoadingSchema(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const enabledFields = (schema.fields || [])
    .filter((f) => f.enabled)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const onChange = (key, val) => setValues((s) => ({ ...s, [key]: val }));

  const validate = () => {
    const e = {};
    enabledFields.forEach((f) => {
      const v = values[f.key];
      if (f.type === "checkbox") {
        if (f.required && !v) e[f.key] = `${f.label} is required.`;
      } else {
        const sv = (v ?? "").toString().trim();
        if (f.required && !sv) e[f.key] = `${f.label} is required.`;
        else if (f.type === "email" && sv && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sv))
          e[f.key] = "Enter a valid email.";
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payloadValues = {};
      enabledFields.forEach((f) => {
        const v = values[f.key];
        if (f.type === "checkbox") {
          payloadValues[f.key] = !!v;
        } else {
          const sv = (v ?? "").toString().trim();
          if (sv) payloadValues[f.key] = sv;
        }
      });
      await api.post("/contacts", { values: payloadValues });
      setSubmitted(true);
      setValues(initialValuesFor(schema.fields));
      toast.success("You're on the list — thanks for reaching out.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit right now. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't submit right now.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = schema.confirmation || DEFAULT_SCHEMA.confirmation;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-32 bg-[#2C1E16] text-[#FAF9F6]"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        {schema.eyebrow && (
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4A373] mb-4">
            {schema.eyebrow}
          </div>
        )}
        <h2
          data-testid="contact-heading"
          className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight"
        >
          {schema.heading}
        </h2>
        {schema.subheading && (
          <p className="mt-5 text-[#FAF9F6]/75 text-base sm:text-lg max-w-2xl">
            {schema.subheading}
          </p>
        )}

        {submitted ? (
          <motion.div
            data-testid="contact-success"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 rounded-3xl border border-[#FAF9F6]/15 bg-[#FAF9F6]/5 p-8 sm:p-10"
          >
            {confirm.eyebrow && (
              <div className="inline-flex items-center gap-2 text-[#D4A373] font-medium">
                <Check size={18} strokeWidth={1.6} />
                {confirm.eyebrow}
              </div>
            )}
            {confirm.title && (
              <p className="mt-3 font-display text-2xl text-[#FAF9F6]">{confirm.title}</p>
            )}
            {confirm.body && (
              <p className="mt-3 text-[#FAF9F6]/80">{confirm.body}</p>
            )}
            {confirm.button_label && (
              <button
                data-testid="contact-send-another"
                type="button"
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sm underline decoration-[#D4A373] underline-offset-4 hover:decoration-white"
              >
                {confirm.button_label}
              </button>
            )}
          </motion.div>
        ) : loadingSchema ? (
          <div className="mt-12 text-[#FAF9F6]/60 text-sm">Loading form…</div>
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="mt-12 grid sm:grid-cols-2 gap-x-8 gap-y-8"
          >
            {enabledFields.map((f) => {
              const fullWidth = f.type === "textarea" || f.type === "checkbox";
              return (
                <FieldRenderer
                  key={f.id || f.key}
                  field={f}
                  value={values[f.key]}
                  onChange={(v) => onChange(f.key, v)}
                  error={errors[f.key]}
                  className={fullWidth ? "sm:col-span-2" : ""}
                />
              );
            })}

            <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              {schema.consent_text && (
                <p className="text-xs text-[#FAF9F6]/55">{schema.consent_text}</p>
              )}
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-[#FAF9F6] text-[#2C1E16] hover:bg-[#D4A373] hover:text-[#2C1E16] disabled:opacity-60 transition-colors rounded-full px-7 py-4 font-medium"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" strokeWidth={1.6} />
                    Sending…
                  </>
                ) : (
                  <>{schema.submit_label || "Submit"}</>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .mc-contact-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(250, 249, 246, 0.25);
          color: #FAF9F6;
          padding: 0.85rem 0;
          font-size: 1rem;
          outline: none;
          transition: border-color 0.2s;
        }
        .mc-contact-input::placeholder { color: rgba(250, 249, 246, 0.35); }
        .mc-contact-input:focus { border-bottom-color: #D4A373; }
        .mc-contact-input option { background: #2C1E16; color: #FAF9F6; }
      `}</style>
    </section>
  );
};

const FieldRenderer = ({ field, value, onChange, error, className = "" }) => {
  const testId = `contact-input-${field.key}`;
  if (field.type === "checkbox") {
    return (
      <label className={`block ${className}`}>
        <span className="inline-flex items-start gap-3 cursor-pointer select-none">
          <input
            data-testid={testId}
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#D4A373]"
          />
          <span className="text-[#FAF9F6] text-sm leading-snug">
            {field.label}
            {field.required && <span className="text-[#D4A373]"> *</span>}
            {field.help_text && (
              <span className="block text-xs text-[#FAF9F6]/55 mt-0.5">{field.help_text}</span>
            )}
          </span>
        </span>
        {error && <FieldError testId={`contact-error-${field.key}`}>{error}</FieldError>}
      </label>
    );
  }
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-[0.18em] text-[#FAF9F6]/55 mb-1">
        {field.label}
        {field.required && <span className="text-[#D4A373]"> *</span>}
      </span>
      {field.type === "textarea" ? (
        <textarea
          data-testid={testId}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={field.placeholder || ""}
          className="mc-contact-input resize-none pt-3"
        />
      ) : field.type === "select" ? (
        <select
          data-testid={testId}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="mc-contact-input"
        >
          <option value="">{field.placeholder || "Choose one…"}</option>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          data-testid={testId}
          type={field.type === "email" ? "email" : field.type === "tel" ? "tel" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className="mc-contact-input"
          autoComplete={
            field.type === "email" ? "email" : field.type === "tel" ? "tel" : "off"
          }
        />
      )}
      {field.help_text && (
        <span className="block text-[11px] text-[#FAF9F6]/55 mt-1">{field.help_text}</span>
      )}
      {error && <FieldError testId={`contact-error-${field.key}`}>{error}</FieldError>}
    </label>
  );
};

const FieldError = ({ children, testId }) => (
  <span data-testid={testId} className="mt-2 block text-xs text-[#D4A373]">
    {children}
  </span>
);
